/**
 * Thinking 思考内容状态管理 Composable
 * 用于管理 AI 消息的思考过程显示和交互
 */

import { ref, nextTick } from 'vue'

export function useThinkingState() {
  // 每条消息的展开状态（messageId -> 是否展开）
  const expandedMap = ref<Record<string, boolean>>({})

  // Thinking 滚动容器引用
  const scrollRefs = ref<Record<string, HTMLElement>>({})

  // 更新定时器
  const updateTimers = ref<Record<string, NodeJS.Timeout>>({})

  // 是否正在更新的状态
  const isUpdatingMap = ref<Record<string, boolean>>({})

  /**
   * 切换思考块展开/收起状态
   */
  const toggleExpanded = (messageId: string) => {
    expandedMap.value[messageId] = !expandedMap.value[messageId]
  }

  /**
   * 设置思考块展开状态
   */
  const setExpanded = (messageId: string, expanded: boolean) => {
    expandedMap.value[messageId] = expanded
  }

  /**
   * 设置 Thinking 滚动容器引用
   */
  const setScrollRef = (messageId: string, el: any) => {
    if (el) {
      scrollRefs.value[messageId] = el as HTMLElement
    }
  }

  /**
   * 滚动到思考内容底部（显示最新内容）
   */
  const scrollToBottom = (messageId: string) => {
    const scrollEl = scrollRefs.value[messageId]
    if (!scrollEl) return

    const innerEl = scrollEl.querySelector('.thinking-text-inner') as HTMLElement
    if (innerEl) {
      // 向下滚动到底部，显示最新内容
      scrollEl.scrollTop = innerEl.scrollHeight - scrollEl.clientHeight
    }
  }

  /**
   * 标记思考内容正在更新
   */
  const markAsUpdating = (messageId: string) => {
    isUpdatingMap.value[messageId] = true

    // 清除之前的定时器
    if (updateTimers.value[messageId]) {
      clearTimeout(updateTimers.value[messageId])
    }

    // 滚动到底部显示最新内容
    nextTick(() => {
      scrollToBottom(messageId)
    })

    // 1秒后如果没有变化，标记为不再更新
    updateTimers.value[messageId] = setTimeout(() => {
      isUpdatingMap.value[messageId] = false
    }, 1000)
  }

  /**
   * 清理指定消息的状态
   */
  const cleanupMessage = (messageId: string) => {
    delete expandedMap.value[messageId]
    delete scrollRefs.value[messageId]
    delete isUpdatingMap.value[messageId]

    if (updateTimers.value[messageId]) {
      clearTimeout(updateTimers.value[messageId])
      delete updateTimers.value[messageId]
    }
  }

  /**
   * 清理所有定时器（组件卸载时调用）
   */
  const cleanup = () => {
    Object.values(updateTimers.value).forEach(timer => {
      if (timer) clearTimeout(timer)
    })
    updateTimers.value = {}
    scrollRefs.value = {}
    expandedMap.value = {}
    isUpdatingMap.value = {}
  }

  return {
    expandedMap,
    scrollRefs,
    isUpdatingMap,
    toggleExpanded,
    setExpanded,
    setScrollRef,
    scrollToBottom,
    markAsUpdating,
    cleanupMessage,
    cleanup
  }
}

